/**
 * The Seller's Broker / Construction License line for the request as it stands (2026-10-23).
 * Reads the division's /refdata (cached by feedRefdata) for each picked community's own state,
 * then asks disclaimerLicense.ts for the line.
 *
 * ── THE FILED LINE MAY NOT DEPEND ON TIMING (Farhan's review of #396, 2026-10-23) ──────────
 * The first cut worked the line out from the markets and the division while /refdata was still
 * loading, and submit used whatever the hook held at that moment. So a fast submit could file
 * the division's default line (Charlotte → NC) for an email about an SC community, and the same
 * request submitted a second later filed the SC line. Fixes:
 *   · the hook returns `null` (the placeholders) until /refdata has answered or failed;
 *   · submit calls `resolveLicenseLine`, which WAITS for /refdata (cached, so usually instant)
 *     before building the line it files;
 *   · if /refdata fails or times out, a picked community's state is unknown, so a division
 *     that sells in more than one state keeps the placeholders (disclaimerLicense.ts
 *     `licenseStates` → null). It never guesses the state from the market or the division.
 *   · a failed hook read retries once, so the preview catches up with what submit will file.
 */
import { useEffect, useMemo, useState } from "react";
import { feedRefdata } from "./echFeeds";
import { licenseLine, licenseStates, RefdataLike } from "./disclaimerLicense";

const csv = (v?: string) => (v ? String(v).split(",").map((s) => s.trim()).filter(Boolean) : []);

export interface LicenseForm { divisionCode?: string; community?: string; masterplan?: string; market?: string | string[] }

const marketsOf = (form: LicenseForm) => (Array.isArray(form.market) ? form.market : csv(form.market));

/** the line for this form, given /refdata (or null when it failed). Never throws. */
export function licenseLineFor(form: LicenseForm, ref: RefdataLike | null): string | null {
  try {
    const div = String(form.divisionCode || "");
    if (!div) return null;
    const states = licenseStates(div, { communities: csv(form.community), masterplans: csv(form.masterplan), markets: marketsOf(form) }, ref);
    if (!states) return null;
    return licenseLine(div, states, ref ? ref.license : null);
  } catch {
    /* a malformed /refdata must never take the preview or the submit down */
    return null;
  }
}

/** at submit: wait for /refdata (up to `timeoutMs`), then build the line that gets filed */
export async function resolveLicenseLine(form: LicenseForm, timeoutMs = 8000): Promise<string | null> {
  const div = String(form.divisionCode || "");
  if (!div) return null;
  let ref: RefdataLike | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    ref = await Promise.race<RefdataLike | null>([
      feedRefdata(div),
      new Promise<null>((res) => { timer = setTimeout(() => res(null), timeoutMs); }),
    ]);
  } catch {
    ref = null;
  } finally {
    if (timer) clearTimeout(timer);
  }
  return licenseLineFor(form, ref);
}

export function useLicenseLine(form: LicenseForm, enabled = true, retryMs = 4000): string | null {
  const div = String(form.divisionCode || "");
  /* `done` = /refdata answered (data) or failed (data null) for THIS division */
  const [ref, setRef] = useState<{ div: string; data: RefdataLike | null; done: boolean }>({ div: "", data: null, done: false });
  useEffect(() => {
    if (!enabled || !div) return;
    let live = true;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const read = (again: boolean) => feedRefdata(div)
      .then((r) => { if (live) setRef({ div, data: r, done: true }); })
      .catch(() => {
        if (!live) return;
        setRef({ div, data: null, done: true });
        /* feedRefdata forgets a failed read, so one later try can succeed — the same read
           submit would make, so the preview and the filed line agree */
        if (again) retry = setTimeout(() => { if (live) read(false); }, retryMs);
      });
    read(true);
    return () => { live = false; if (retry) clearTimeout(retry); };
  }, [div, enabled, retryMs]);
  const ready = ref.div === div && ref.done;
  const key = [div, form.community || "", form.masterplan || "", marketsOf(form).join("|"), ready ? "r" : "", ref.data ? "d" : ""].join("§");
  return useMemo(() => (ready ? licenseLineFor(form, ref.data) : null), [key]); // eslint-disable-line react-hooks/exhaustive-deps
}
